import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { t, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, day, money, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { Icon } from './Icon.tsx';
import { Button, Confirm, IconButton, Skeleton } from './ui.tsx';

interface Person {
  id: string;
  name: string;
  username: string;
}
interface Change {
  field: string;
  label: string;
  type: string;
  from: unknown;
  to: unknown;
}
type Item =
  | { kind: 'note'; id: string; author: string; author_name: string | null; body: string; at: string; edited: boolean; mine: boolean }
  | { kind: 'change'; id: string; action: 'create' | 'update' | 'delete'; author_name: string | null; at: string; changes: Change[] };
interface Feed {
  title: string;
  items: Item[];
  followers: { id: string; name: string }[];
  following: boolean;
  people: Person[];
}

const TONES = ['blue', 'green', 'orange', 'purple', 'teal', 'red', 'yellow'];
const toneOf = (id: string | null) => TONES[id ? [...id].reduce((a, c) => a + c.charCodeAt(0), 0) % TONES.length : 0]!;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');

/** A changed value as a person reads it: amounts as money, dates as dates, choices as words. */
function valueText(type: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return t('rn.empty');
  if (type === 'money' && typeof v === 'number') return money(v);
  if (type === 'bool') return v ? t('rn.yes') : t('rn.no');
  if (type === 'date' && typeof v === 'string') return day(v);
  if (type === 'datetime' && typeof v === 'string') return dateTime(v);
  if (type === 'enum' && typeof v === 'string') return v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ');
  const s = String(v);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

/** Note text with @mentions picked out; nothing else is interpreted. */
function NoteBody({ text, people, me }: { text: string; people: Map<string, Person>; me: string | null }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(/(^|[\s(])@([a-z0-9._-]{2,40})/gi)) {
    const who = people.get(m[2]!.toLowerCase());
    if (!who) continue;
    const at = m.index! + m[1]!.length;
    parts.push(text.slice(last, at));
    parts.push(
      <span key={at} className={`ds-mention${who.id === me ? ' me' : ''}`} title={who.name}>
        @{who.username}
      </span>,
    );
    last = at + 1 + m[2]!.length;
  }
  parts.push(text.slice(last));
  return <p className="rn-body">{parts}</p>;
}

type Filter = 'all' | 'notes' | 'changes';
const FILTERS: { key: Filter; label: MessageKey }[] = [
  { key: 'all', label: 'rn.all' },
  { key: 'notes', label: 'rn.notes' },
  { key: 'changes', label: 'rn.changes' },
];

/**
 * Notes and history at the foot of a record (an invoice, an order, a customer, a lead): the team
 * writes notes with @mentions, follows the record, and sees who created it and what changed.
 * Shown only to people with the team chat (Discuss); notes are never printed or sent to customers.
 */
export function RecordNotes({ model, record, version }: { model: string; record: string; version?: unknown }) {
  const { can, me: who } = useSession();
  const { fail, toast } = useUi();
  const me = who?.userId ?? null;
  const enabled = can('discuss.use');
  const [feed, setFeed] = useState<Feed | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    () =>
      action<Feed>('discuss.record', { model, record }).then(setFeed, (e: { status?: number }) => {
        // A record of a kind the person may not see simply shows no notes.
        if (e?.status !== 403 && e?.status !== 404) fail(e);
      }),
    [model, record, fail],
  );
  useEffect(() => {
    if (!enabled) return;
    void load();
    return subscribe(['discuss'], () => void load());
  }, [enabled, load, version]);
  if (!enabled) return null;

  const people = new Map((feed?.people ?? []).map((p) => [p.username.toLowerCase(), p]));
  const items = (feed?.items ?? []).filter((i) => filter === 'all' || (filter === 'notes' ? i.kind === 'note' : i.kind === 'change'));
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      await load();
      if (done) toast(done);
      return true;
    } catch (e) {
      fail(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rn" aria-labelledby={`rn-${record}`}>
      <header className="rn-head">
        <h3 id={`rn-${record}`}>
          <Icon name="messages" size={16} /> {t('rn.title')}
        </h3>
        {feed ? (
          <div className="rn-follow">
            {feed.followers.length ? (
              <span className="rn-followers" title={feed.followers.map((f) => f.name).join(', ')} aria-label={t('rn.followed_by', { names: feed.followers.map((f) => f.name).join(', ') })}>
                {feed.followers.slice(0, 4).map((f) => (
                  <span key={f.id} className={`ds-avatar sm tone-${toneOf(f.id)}`} aria-hidden="true">
                    {initials(f.name)}
                  </span>
                ))}
                {feed.followers.length > 4 ? <span className="rn-more">+{feed.followers.length - 4}</span> : null}
              </span>
            ) : null}
            <Button
              size="sm"
              variant={feed.following ? 'ghost' : 'secondary'}
              icon={feed.following ? 'bellOff' : 'bell'}
              busy={busy}
              onClick={() => void act(() => action('discuss.follow', { model, record, follow: !feed.following }), feed.following ? t('rn.unfollowed') : t('rn.followed'))}
            >
              {feed.following ? t('rn.unfollow') : t('rn.follow')}
            </Button>
          </div>
        ) : null}
      </header>
      {!feed ? (
        <Skeleton h={96} r="var(--radius-md)" />
      ) : (
        <>
          <Composer people={feed.people} onPost={(body) => act(() => action('discuss.post_note', { model, record, body }), t('rn.posted'))} />
          <div className="segmented rn-filter" role="radiogroup" aria-label={t('rn.show')}>
            {FILTERS.map((f) => (
              <button key={f.key} type="button" role="radio" aria-checked={filter === f.key} className={filter === f.key ? 'active' : ''} onClick={() => setFilter(f.key)}>
                {t(f.label)}
              </button>
            ))}
          </div>
          {items.length ? (
            <ol className="rn-list">
              {items.map((i) =>
                i.kind === 'note' ? (
                  <li key={i.id} className={`rn-note${i.mine ? ' mine' : ''}`}>
                    <span className={`ds-avatar sm tone-${toneOf(i.author)}`} aria-hidden="true">
                      {initials(i.author_name ?? '?')}
                    </span>
                    <div className="rn-note-box">
                      <div className="rn-meta">
                        <b>{i.author_name ?? t('rn.someone')}</b>
                        <time dateTime={i.at} title={dateTime(i.at)}>
                          {since(i.at)}
                        </time>
                        {i.edited ? <span className="muted">· {t('rn.edited')}</span> : null}
                        {i.mine && editing !== i.id ? (
                          <span className="rn-tools">
                            <IconButton icon="edit" label={t('rn.edit')} onClick={() => setEditing(i.id)} />
                            <IconButton icon="trash" label={t('rn.delete')} onClick={() => setDeleting(i.id)} />
                          </span>
                        ) : null}
                      </div>
                      {editing === i.id ? (
                        <EditBox
                          initial={i.body}
                          busy={busy}
                          onCancel={() => setEditing(null)}
                          onSave={async (body) => (await act(() => action('discuss.edit_note', { id: i.id, body }), t('rn.saved'))) && setEditing(null)}
                        />
                      ) : (
                        <NoteBody text={i.body} people={people} me={me} />
                      )}
                    </div>
                  </li>
                ) : (
                  <li key={i.id} className="rn-change">
                    <span className="rn-dot" aria-hidden="true">
                      <Icon name={i.action === 'create' ? 'plus' : 'edit'} size={12} />
                    </span>
                    <div>
                      <div className="rn-meta">
                        <b>{i.author_name ?? t('rn.system')}</b>
                        <span>{i.action === 'create' ? t('rn.created') : i.action === 'delete' ? t('rn.deleted_record') : t('rn.changed')}</span>
                        <time dateTime={i.at} title={dateTime(i.at)}>
                          {since(i.at)}
                        </time>
                      </div>
                      {i.changes.length ? (
                        <ul className="rn-diff">
                          {i.changes.map((c) => (
                            <li key={c.field}>
                              <span className="muted">{c.label}</span>
                              {c.type === 'ref' ? null : (
                                <>
                                  <s>{valueText(c.type, c.from)}</s>
                                  <Icon name="chevronRight" size={12} />
                                  <span>{valueText(c.type, c.to)}</span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </li>
                ),
              )}
            </ol>
          ) : (
            <p className="muted rn-empty">{filter === 'changes' ? t('rn.no_changes') : t('rn.no_notes')}</p>
          )}
        </>
      )}
      {deleting ? (
        <Confirm
          title={t('rn.delete_title')}
          body={t('rn.delete_body')}
          confirm={t('rn.delete')}
          danger
          busy={busy}
          onConfirm={async () => {
            await act(() => action('discuss.delete_note', { id: deleting }), t('rn.deleted'));
            setDeleting(null);
          }}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </section>
  );
}

/** Where a note is written; "@mi" offers the people whose name or username starts that way. */
function Composer({ people, onPost }: { people: Person[]; onPost: (body: string) => Promise<boolean> }) {
  const [body, setBody] = useState('');
  const [caret, setCaret] = useState(0);
  const [pick, setPick] = useState(0);
  const [sending, setSending] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const token = /(?:^|[\s(])@([a-z0-9._-]*)$/i.exec(body.slice(0, caret));
  const q = token?.[1]?.toLowerCase() ?? '';
  const matches = token
    ? people
        .filter(
          (p) =>
            p.username.toLowerCase().startsWith(q) ||
            p.name
              .toLowerCase()
              .split(/\s+/)
              .some((w) => w.startsWith(q)),
        )
        .slice(0, 6)
    : [];
  useEffect(() => setPick(0), [q]);
  const choose = (p: Person) => {
    const start = caret - q.length;
    const next = `${body.slice(0, start)}${p.username} ${body.slice(caret)}`;
    setBody(next);
    const pos = start + p.username.length + 1;
    requestAnimationFrame(() => {
      input.current?.setSelectionRange(pos, pos);
      input.current?.focus();
      setCaret(pos);
    });
  };
  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    if (await onPost(text)) setBody('');
    setSending(false);
  };
  return (
    <form
      className="rn-composer"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      {matches.length ? (
        <ul className="ds-suggest" role="listbox" aria-label={t('rn.mention')}>
          {matches.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === pick}>
              <button type="button" className={i === pick ? 'active' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(p)}>
                <span className={`ds-avatar sm tone-${toneOf(p.id)}`} aria-hidden="true">
                  {initials(p.name)}
                </span>
                <strong>{p.name}</strong>
                <span className="muted">@{p.username}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <textarea
        ref={input}
        className="input textarea"
        rows={Math.min(6, Math.max(2, body.split('\n').length))}
        maxLength={4000}
        value={body}
        placeholder={t('rn.placeholder')}
        aria-label={t('rn.write')}
        onChange={(e) => {
          setBody(e.target.value);
          setCaret(e.target.selectionStart);
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
        onKeyDown={(e) => {
          if (matches.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setPick((p) => (p + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
          } else if (matches.length && (e.key === 'Enter' || e.key === 'Tab')) {
            e.preventDefault();
            choose(matches[pick]!);
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void send();
          }
        }}
      />
      <div className="rn-composer-foot">
        <span className="muted small">
          <Icon name="lock" size={12} /> {t('rn.private')}
        </span>
        <Button type="submit" size="sm" variant="primary" icon="send" busy={sending} disabled={!body.trim()}>
          {t('rn.add')}
        </Button>
      </div>
    </form>
  );
}

function EditBox({ initial, busy, onCancel, onSave }: { initial: string; busy: boolean; onCancel: () => void; onSave: (body: string) => void }) {
  const [body, setBody] = useState(initial);
  return (
    <div className="rn-edit">
      <textarea className="input textarea" rows={3} maxLength={4000} value={body} aria-label={t('rn.edit')} onChange={(e) => setBody(e.target.value)} data-autofocus />
      <div className="rn-edit-foot">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" variant="primary" busy={busy} disabled={!body.trim()} onClick={() => onSave(body.trim())}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
